import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_EMPLOYEES } from '../../../config/collections'
import { loadEmployeesList } from '../../../lib/pocketbaseEmployees'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { useAuth } from '../../../context/AuthContext'
import { isSupabaseAuth } from '../../../lib/supabase'
import { matchesListSearch } from '../../../lib/listSearch'
import type { EmployeeRecord } from '../../../types/records'
import { Alert, ButtonLink, PageHeader, PageShell, SearchField } from '../../../components/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

export function EmployeesPage() {
  const { user, can } = useAuth()
  const [items, setItems] = useState<EmployeeRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await loadEmployeesList(1, 100)
      setItems(list.items)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    if (isSupabaseAuth()) return
    if (!window.confirm('Удалить пользователя?')) return
    if (id === user?.id) {
      alert('Нельзя удалить свою учётную запись.')
      return
    }
    try {
      await pb.collection(COL_EMPLOYEES).delete(id)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка удаления'))
    }
  }

  const crm = isSupabaseAuth()

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.email,
        row.name,
        row.roleName,
        row.expand?.role?.title,
      ),
    )
  }, [items, searchQuery])

  return (
    <PageShell>
      <PageHeader
        title="Пользователи"
        lead={
          crm
            ? 'Справочник из CRM. Добавление и редактирование — только в CRM.'
            : 'Сотрудники с доступом к СУП.'
        }
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.USERS_WRITE) && !crm && (
          <ButtonLink to="/admin/administration/employees/new" variant="primary">
            Добавить
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по email, имени, роли…"
            aria-label="Поиск пользователей"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Email</th>
              <th className={tableHeadClass}>Имя</th>
              <th className={tableHeadClass}>Роль</th>
              <th className={tableHeadClass}>Название роли</th>
              {!crm && <th className={tableHeadClass} />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={crm ? 4 : 5}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={crm ? 4 : 5}>
                  <em>{items.length === 0 ? 'Нет записей' : 'Ничего не найдено'}</em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{row.email}</td>
                  <td className={tableCellClass}>{row.name}</td>
                  <td className={tableCellClass}>{row.roleName}</td>
                  <td className={tableCellClass}>{row.expand?.role?.title || '—'}</td>
                  {!crm && (
                    <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                      {can(PERMISSION_KEYS.USERS_WRITE) && (
                        <Link to={`/admin/administration/employees/${row.id}`} className={btn('ghost')}>
                          Изменить
                        </Link>
                      )}
                      {can(PERMISSION_KEYS.USERS_DELETE) && (
                        <button
                          type="button"
                          className={cn(btn('danger'), 'ml-1')}
                          onClick={() => void removeRow(row.id)}
                          disabled={row.id === user?.id}
                        >
                          Удалить
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
